import { Link } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { useCallback, useMemo } from "react";

import { useIsMobile } from "../lib/hooks/use-breakpoints";
import { hoverCategorySlugStore } from "../lib/store";
import type { Category, VoidComponent } from "../lib/types";
import { booleanAttribute } from "../lib/utils";
import { Icon } from "./icon";

interface CategoryListItemProps {
	category: Category;
	pillarSlug: string;
}

const CategoryListItem: VoidComponent<CategoryListItemProps> = ({ category, pillarSlug }) => {
	const hoveredCategorySlug = useStore(hoverCategorySlugStore);
	const isMobile = useIsMobile();

	const categorySlug = category.slug.current;

	const linkParams = useMemo(
		() => ({ pillar: pillarSlug, category: categorySlug }),
		[pillarSlug, categorySlug],
	);

	const onMouseEnter = useCallback(() => {
		if (isMobile) {
			return;
		}

		hoverCategorySlugStore.setState(categorySlug);
	}, [categorySlug, isMobile]);

	const onMouseLeave = useCallback(() => {
		if (isMobile) {
			return;
		}

		hoverCategorySlugStore.setState(null);
	}, [isMobile]);

	return (
		<li key={category._key}>
			<Link
				to="/$pillar/$category"
				params={linkParams}
				className="flex h-18 items-start gap-4 bg-white p-4 text-foreground focus-visible:bg-accent focus-visible:text-surface-primary active:bg-accent active:text-surface-primary desktop:h-24 desktop:transition-colors desktop:duration-200 desktop:hover:bg-accent desktop:hover:text-surface-primary desktop:data-hover:bg-accent desktop:data-hover:text-surface-primary"
				data-hover={booleanAttribute(hoveredCategorySlug === categorySlug)}
				data-disable-focus-ring=""
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				<p className="flex-1 text-sm desktop:text-xl">{category.title}</p>

				<Icon name="arrow-right" className="mt-0.5 size-5 shrink-0 desktop:mt-1" />
			</Link>
		</li>
	);
};

export { CategoryListItem };
